import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, CalendarDays, Calendar, CalendarRange, CalendarCheck,
  FileText, Download, Printer, Mail, Search, RefreshCw, TrendingUp,
  TrendingDown, DollarSign, ShoppingCart, Users, Package, CreditCard,
  BarChart4, PieChart, LineChart, AreaChart, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import StatCard from '../components/common/StatCard';
import Badge from '../components/common/Badge';
import EmptyState from '../components/common/EmptyState';
import Skeleton from '../components/common/Skeleton';
import ReportChart from '../components/reports/ReportChart';
import ReportFilters from '../components/reports/ReportFilters';
import ReportTable from '../components/reports/ReportTable';
import KpiGrid from '../components/reports/KpiGrid';
import {
  fetchDashboard, fetchLatestDay, fetchDailyReport, fetchWeeklyReport, fetchMonthlyReport,
  fetchYearlyReport, fetchPaymentAnalytics, fetchTopProducts, fetchTopCustomers,
  fetchProfitAnalytics, generateCustomReport, clearReports
} from '../redux/slices/reportSlice';
import { fetchCustomers } from '../redux/slices/customerSlice';
import { fetchProducts } from '../redux/slices/productSlice';

const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'daily', label: 'Daily', icon: CalendarDays },
  { id: 'weekly', label: 'Weekly', icon: Calendar },
  { id: 'monthly', label: 'Monthly', icon: CalendarRange },
  { id: 'yearly', label: 'Yearly', icon: CalendarCheck },
  { id: 'custom', label: 'Custom', icon: BarChart4 },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const getPakistanToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Karachi' }).format(new Date());

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', damping: 20, stiffness: 200 } },
};

export default function Reports() {
  const dispatch = useDispatch();
  const {
    dashboard, latestDay, dailyReport, weeklyReport, monthlyReport, yearlyReport,
    paymentAnalytics, topProducts, topCustomers, profitAnalytics, customReport,
    loading
  } = useSelector((state) => state.reports);
  const { customers } = useSelector((state) => state.customers);
  const { products } = useSelector((state) => state.products);
  const { users } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [datePicker, setDatePicker] = useState(getPakistanToday);
  const [customFilters, setCustomFilters] = useState(null);
  const [customChartType, setCustomChartType] = useState('bar');
  const [customGroupBy, setCustomGroupBy] = useState('day');
  const [exporting, setExporting] = useState(false);
  const datePickerInitialized = useRef(false);

  useEffect(() => {
    dispatch(fetchCustomers({ limit: 200 }));
    dispatch(fetchProducts({ limit: 200 }));
    dispatch(fetchLatestDay());
  }, [dispatch]);

  useEffect(() => {
    if (datePickerInitialized.current || !latestDay?.date) return;
    datePickerInitialized.current = true;
    if (latestDay.date !== datePicker) setDatePicker(latestDay.date);
  }, [latestDay, datePicker]);

  useEffect(() => {
    switch (activeTab) {
      case 'dashboard':
        dispatch(fetchDashboard());
        break;
      case 'daily':
        dispatch(fetchDailyReport(datePicker));
        break;
      case 'weekly':
        dispatch(fetchWeeklyReport({}));
        break;
      case 'monthly':
        dispatch(fetchMonthlyReport({}));
        dispatch(fetchTopProducts({ limit: 10 }));
        dispatch(fetchPaymentAnalytics({ period: 'month' }));
        dispatch(fetchTopCustomers({ limit: 10 }));
        break;
      case 'yearly':
        dispatch(fetchYearlyReport({}));
        dispatch(fetchProfitAnalytics({ period: 'year' }));
        break;
      default:
        break;
    }
  }, [dispatch, activeTab, datePicker]);

  const handleCustomReport = useCallback((filters) => {
    setCustomFilters(filters);
    dispatch(generateCustomReport({ ...filters, groupBy: customGroupBy }));
  }, [dispatch, customGroupBy]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      await new Promise((r) => setTimeout(r, 1000));
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const renderDashboard = () => {
    if (loading && !dashboard) return [...Array(6)].map((_, i) => <Skeleton key={i} variant="card" />);
    const kpiCards = [
      { key: 'totalSales', label: 'Total Sales', value: dashboard?.totalSales || 0, icon: DollarSign, color: 'blue', trend: dashboard?.salesGrowth },
      { key: 'totalProfit', label: 'Total Profit', value: dashboard?.totalProfit || 0, icon: TrendingUp, color: 'green', trend: dashboard?.profitGrowth },
      { key: 'totalTransactions', label: 'Transactions', value: dashboard?.totalTransactions || 0, icon: ShoppingCart, color: 'purple', trend: dashboard?.transactionGrowth },
      { key: 'activeCustomers', label: 'Active Customers', value: dashboard?.activeCustomers || 0, icon: Users, color: 'cyan', trend: dashboard?.customerGrowth },
      { key: 'avgOrderValue', label: 'Avg Order Value', value: dashboard?.avgOrderValue || 0, icon: CreditCard, color: 'yellow' },
      { key: 'stockValue', label: 'Stock Value', value: dashboard?.stockValue || 0, icon: Package, color: 'red', trend: dashboard?.stockGrowth },
    ];
    return (
      <div className="space-y-6">
        <KpiGrid cards={kpiCards} columns={6} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ReportChart
            type="line"
            data={dashboard?.salesTrend || []}
            dataKeys={['sales']}
            xAxisKey="date"
            title="Sales Trend"
            loading={loading}
          />
          <ReportChart
            type="area"
            data={dashboard?.revenueProfit || []}
            dataKeys={['revenue', 'profit']}
            xAxisKey="date"
            title="Revenue vs Profit"
            loading={loading}
          />
        </div>
      </div>
    );
  };

  const renderDaily = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <CalendarDays size={18} className="text-gray-400" />
        <input
          type="date"
          value={datePicker}
          onChange={(e) => setDatePicker(e.target.value)}
          className="px-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500"
        />
        <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => dispatch(fetchDailyReport(datePicker))}>
          Refresh
        </Button>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton variant="chart" />
          <Skeleton variant="chart" />
          <Skeleton variant="card" className="lg:col-span-2" />
        </div>
      ) : dailyReport ? (
        <div className="space-y-6">
          <KpiGrid
            cards={[
              { key: 'sales', label: 'Sales', value: dailyReport.totalSales || 0, icon: DollarSign, color: 'blue' },
              { key: 'profit', label: 'Profit', value: dailyReport.totalProfit || 0, icon: TrendingUp, color: 'green' },
              { key: 'transactions', label: 'Transactions', value: dailyReport.transactionCount || 0, icon: ShoppingCart, color: 'purple' },
              { key: 'customers', label: 'Customers', value: dailyReport.customerCount || 0, icon: Users, color: 'cyan' },
              { key: 'items', label: 'Items Sold', value: dailyReport.totalItems ?? dailyReport.totalQuantity ?? 0, icon: Package, color: 'amber' },
            ]}
            columns={4}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReportChart type="line" data={dailyReport.hourlyData || []} dataKeys={['sales']} xAxisKey="hour" title="Hourly Sales" />
            <ReportChart type="bar" data={dailyReport.productBreakdown || []} dataKeys={['value']} xAxisKey="name" title="Top Products" />
          </div>
          <div className="flex items-center gap-2">
            <Users size={16} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Customers - Items Sold</h3>
          </div>
          <ReportTable
            columns={[
              { key: 'name', header: 'Customer' },
              { key: 'phoneNumber', header: 'Phone' },
              { key: 'orders', header: 'Orders' },
              { key: 'items', header: 'Items Sold' },
              { key: 'amount', header: 'Amount', render: (v) => `Rs. ${(v || 0).toLocaleString()}` },
            ]}
            data={[...(dailyReport.customerBreakdown || [])].sort((a, b) => b.amount - a.amount)}
          />
          <ReportTable
            columns={[
              { key: 'time', header: 'Time', render: (v) => <span className="text-gray-900 dark:text-white">{v}</span> },
              { key: 'product', header: 'Product' },
              { key: 'quantity', header: 'Qty' },
              { key: 'amount', header: 'Amount', render: (v) => `Rs. ${(v || 0).toLocaleString()}` },
              { key: 'paymentMethod', header: 'Payment' },
            ]}
            data={dailyReport.transactions || []}
            loading={loading}
            onExportPDF={() => handleExport('pdf')}
            onExportExcel={() => handleExport('excel')}
            onExportCSV={() => handleExport('csv')}
            onPrint={() => toast.success('Printing...')}
          />
        </div>
      ) : (
        <EmptyState title="No data for this date" description="Select a different date to view the daily report" />
      )}
    </div>
  );

  const renderWeekly = () => (
    <div className="space-y-6">
      {loading ? (
        <>
          <Skeleton variant="chart" />
          <Skeleton variant="chart" />
          <Skeleton variant="card" />
        </>
      ) : weeklyReport ? (
        <div className="space-y-6">
          <KpiGrid
            cards={[
              { key: 'totalSales', label: 'Weekly Sales', value: weeklyReport.totalSales || 0, icon: DollarSign, color: 'blue' },
              { key: 'avgDaily', label: 'Avg Daily', value: weeklyReport.avgDaily || 0, icon: TrendingUp, color: 'green' },
              { key: 'bestDay', label: 'Best Day', value: weeklyReport.bestDay?.sales || 0, icon: ArrowUpRight, color: 'emerald' },
              { key: 'worstDay', label: 'Worst Day', value: weeklyReport.worstDay?.sales || 0, icon: ArrowDownRight, color: 'red' },
            ]}
            columns={4}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReportChart type="line" data={weeklyReport.dailyTrend || []} dataKeys={['sales']} xAxisKey="day" title="7-Day Trend" />
            <ReportChart type="bar" data={weeklyReport.dailyComparison || []} dataKeys={['thisWeek', 'lastWeek']} xAxisKey="day" title="Week Comparison" />
          </div>
          {weeklyReport.bestDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-5 border border-emerald-200 dark:border-emerald-800/30">
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">Best Day</p>
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{weeklyReport.bestDay.day}</p>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Rs. {(weeklyReport.bestDay.sales || 0).toLocaleString()}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-5 border border-red-200 dark:border-red-800/30">
                <p className="text-xs text-red-600 dark:text-red-400 mb-1">Worst Day</p>
                <p className="text-lg font-bold text-red-700 dark:text-red-300">{weeklyReport.worstDay?.day}</p>
                <p className="text-sm text-red-600 dark:text-red-400">Rs. {(weeklyReport.worstDay?.sales || 0).toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <EmptyState title="No weekly data" description="Weekly report data will appear here" />
      )}
    </div>
  );

  const renderMonthly = () => (
    <div className="space-y-6">
      {loading ? (
        <>
          <Skeleton variant="chart" />
          <Skeleton variant="chart" />
          <Skeleton variant="card" />
        </>
      ) : monthlyReport ? (
        <div className="space-y-6">
          <KpiGrid
            cards={[
              { key: 'totalSales', label: 'Monthly Sales', value: monthlyReport.totalSales || 0, icon: DollarSign, color: 'blue' },
              { key: 'totalProfit', label: 'Profit', value: monthlyReport.totalProfit || 0, icon: TrendingUp, color: 'green' },
              { key: 'avgDaily', label: 'Avg Daily', value: monthlyReport.avgDaily || 0, icon: TrendingDown, color: 'purple' },
              { key: 'growth', label: 'Growth', value: monthlyReport.growth || 0, icon: BarChart4, color: 'yellow', trend: monthlyReport.growth },
            ]}
            columns={4}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReportChart type="line" data={monthlyReport.dailyTrend || []} dataKeys={['sales']} xAxisKey="date" title="30-Day Trend" />
            <ReportChart type="pie" data={paymentAnalytics?.distribution || []} dataKeys={['value']} xAxisKey="name" title="Payment Breakdown" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReportChart type="bar" data={topProducts.slice(0, 10)} dataKeys={['value']} xAxisKey="name" title="Top 10 Products" />
            <ReportChart type="bar" data={topCustomers.slice(0, 10)} dataKeys={['value']} xAxisKey="name" title="Top Customers" />
          </div>
        </div>
      ) : (
        <EmptyState title="No monthly data" description="Monthly report data will appear here" />
      )}
    </div>
  );

  const renderYearly = () => (
    <div className="space-y-6">
      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton variant="chart" />
          <Skeleton variant="chart" />
          <Skeleton variant="card" className="lg:col-span-2" />
        </div>
      ) : yearlyReport ? (
        <div className="space-y-6">
          <KpiGrid
            cards={[
              { key: 'ytdSales', label: 'YTD Sales', value: yearlyReport.ytdSales || 0, icon: DollarSign, color: 'blue' },
              { key: 'ytdProfit', label: 'YTD Profit', value: yearlyReport.ytdProfit || 0, icon: TrendingUp, color: 'green' },
              { key: 'ytdGrowth', label: 'Annual Growth', value: yearlyReport.ytdGrowth || 0, icon: TrendingUp, color: 'purple', trend: yearlyReport.ytdGrowth },
              { key: 'totalTransactions', label: 'Total Transactions', value: yearlyReport.totalTransactions || 0, icon: ShoppingCart, color: 'cyan' },
            ]}
            columns={4}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReportChart type="area" data={yearlyReport.monthlyData || []} dataKeys={['sales', 'profit']} xAxisKey="month" title="12-Month Comparison" />
            <ReportChart type="bar" data={yearlyReport.annualGrowth || []} dataKeys={['growth']} xAxisKey="year" title="Annual Growth" />
          </div>
          <ReportTable
            columns={[
              { key: 'month', header: 'Month' },
              { key: 'sales', header: 'Sales', render: (v) => `Rs. ${(v || 0).toLocaleString()}` },
              { key: 'profit', header: 'Profit', render: (v) => `Rs. ${(v || 0).toLocaleString()}` },
              { key: 'transactions', header: 'Transactions' },
              { key: 'growth', header: 'Growth', render: (v) => `${(v || 0).toFixed(1)}%` },
            ]}
            data={yearlyReport.monthlyBreakdown || []}
            loading={loading}
            onExportPDF={() => handleExport('pdf')}
            onExportExcel={() => handleExport('excel')}
            onExportCSV={() => handleExport('csv')}
            onPrint={() => toast.success('Printing...')}
          />
        </div>
      ) : (
        <EmptyState title="No yearly data" description="Yearly report data will appear here" />
      )}
    </div>
  );

  const renderCustom = () => (
    <div className="space-y-6">
      <ReportFilters
        onApply={handleCustomReport}
        onReset={() => dispatch(clearReports())}
        customers={customers}
        categories={products.map((p) => p.category).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i)}
        staff={users}
        loading={loading}
      />
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Chart Type:</span>
          <select
            value={customChartType}
            onChange={(e) => setCustomChartType(e.target.value)}
            className="px-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none"
          >
            <option value="bar">Bar Chart</option>
            <option value="line">Line Chart</option>
            <option value="area">Area Chart</option>
            <option value="pie">Pie Chart</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Group By:</span>
          <select
            value={customGroupBy}
            onChange={(e) => setCustomGroupBy(e.target.value)}
            className="px-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 focus:outline-none"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="product">Product</option>
            <option value="category">Category</option>
            <option value="customer">Customer</option>
          </select>
        </div>
        <div className="flex-1" />
        <Button variant="secondary" size="sm" icon={Download} onClick={() => handleExport('pdf')} loading={exporting}>
          PDF
        </Button>
        <Button variant="secondary" size="sm" icon={Download} onClick={() => handleExport('excel')} loading={exporting}>
          Excel
        </Button>
        <Button variant="secondary" size="sm" icon={Download} onClick={() => handleExport('csv')} loading={exporting}>
          CSV
        </Button>
        <Button variant="secondary" size="sm" icon={Printer} onClick={() => toast.success('Printing...')}>
          Print
        </Button>
        <Button variant="secondary" size="sm" icon={Mail} onClick={() => toast.success('Report emailed')}>
          Email
        </Button>
      </div>
      {customReport ? (
        <div className="space-y-6">
          <ReportChart type={customChartType} data={customReport.data || []} dataKeys={['value']} xAxisKey="name" title="Custom Report" />
          <ReportTable
            columns={[
              { key: 'name', header: customGroupBy.charAt(0).toUpperCase() + customGroupBy.slice(1) },
              { key: 'value', header: 'Value', render: (v) => `Rs. ${(v || 0).toLocaleString()}` },
              { key: 'count', header: 'Count' },
            ]}
            data={customReport.data || []}
            loading={loading}
            onExportPDF={() => handleExport('pdf')}
            onExportExcel={() => handleExport('excel')}
            onExportCSV={() => handleExport('csv')}
            onPrint={() => toast.success('Printing...')}
          />
        </div>
      ) : (
        <EmptyState
          icon={BarChart4}
          title="Custom Report"
          description="Select filters and click 'Apply Filters' to generate your custom report"
        />
      )}
    </div>
  );

  const tabContent = {
    dashboard: renderDashboard,
    daily: renderDaily,
    weekly: renderWeekly,
    monthly: renderMonthly,
    yearly: renderYearly,
    custom: renderCustom,
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 md:p-6 space-y-6"
    >
      <PageHeader
        title="Reports & Analytics"
        description="Comprehensive business insights and reports"
      />

      <div className="flex overflow-x-auto gap-1 pb-1 scrollbar-thin">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {tabContent[activeTab]?.() || null}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
